export type SupportedLanguage = 'pt-BR' | 'en-US' | 'es-ES'
export const DEFAULT_LANGUAGE: SupportedLanguage = 'pt-BR'
export const MANUAL_LANGUAGE_KEY = 'goldenax-user-language'

export function normalizeLanguage(value?: string | null): SupportedLanguage | null {
  const base = value?.trim().toLowerCase().replaceAll('_', '-').split('-')[0]
  return base === 'pt' ? 'pt-BR' : base === 'en' ? 'en-US' : base === 'es' ? 'es-ES' : null
}

export function browserLanguage(languages: readonly string[] = [], language?: string): SupportedLanguage {
  for (const candidate of [...languages, language]) {
    const supported = normalizeLanguage(candidate)
    if (supported) return supported
  }
  return DEFAULT_LANGUAGE
}

type PreferenceStorage = Pick<Storage, 'getItem' | 'setItem'>
let sessionChoice: SupportedLanguage | null = null

function storage(): PreferenceStorage | undefined {
  try { return typeof window === 'undefined' ? undefined : window.localStorage } catch { return undefined }
}

export function manualLanguage(source: PreferenceStorage | undefined = storage()): SupportedLanguage | null {
  if (sessionChoice) return sessionChoice
  try { return normalizeLanguage(source?.getItem(MANUAL_LANGUAGE_KEY)) } catch { return null }
}

export function chooseLanguage(language: SupportedLanguage, target: PreferenceStorage | undefined = storage()): void {
  sessionChoice = language
  try { target?.setItem(MANUAL_LANGUAGE_KEY, language) } catch {
    // Selection still works for this session when storage is unavailable.
  }
}

export function initialLanguage(): SupportedLanguage {
  return manualLanguage() ?? browserLanguage(
    typeof navigator === 'undefined' ? [] : navigator.languages,
    typeof navigator === 'undefined' ? undefined : navigator.language
  )
}

type UserLanguageContext = { idioma?: string | null; filialId?: string | null; empresaId?: string | null }
type LanguageSources = {
  manual: () => SupportedLanguage | null
  browser: () => SupportedLanguage
  filial: (id: string) => Promise<{ idioma?: string | null; empresaId?: string | null }>
  empresa: (id: string) => Promise<{ idiomaPadrao?: string | null }>
}

export async function resolveLanguage(user: UserLanguageContext | null | undefined, sources: LanguageSources): Promise<SupportedLanguage> {
  const explicit = sources.manual()
  if (explicit) return explicit
  const personal = normalizeLanguage(user?.idioma)
  if (personal) return personal
  let companyId = user?.empresaId
  if (user?.filialId) {
    try {
      const branch = await sources.filial(user.filialId)
      const branchLanguage = normalizeLanguage(branch.idioma)
      if (branchLanguage) return sources.manual() ?? branchLanguage
      companyId = branch.empresaId ?? companyId
    } catch {
      // Use the authenticated user's company if the branch cannot be read.
    }
  }
  if (companyId) {
    try {
      const company = await sources.empresa(companyId)
      const companyLanguage = normalizeLanguage(company.idiomaPadrao)
      if (companyLanguage) return sources.manual() ?? companyLanguage
    } catch {
      // Optional preference requests must never prevent login or language selection.
    }
  }
  return sources.manual() ?? sources.browser()
}
